import React, { FunctionComponent } from "react";
import { Meter } from "src/components";
import { CaptionBody, SmallBody, SmallBodyBolder } from "src/components/Text";
import { localize } from "src/l10n";
import { Label } from "src/ui";
import Visage2Icon from "src/visage2/Visage2Icon/Visage2Icon";
import "./KeyPointIndicatorWidget.scss";
import { formatDiffPercent, formatKeyFigure, parseKeyNumber } from "./keyFigure";

interface IProps {
    primaryValue: string | string[];
    secondaryValue: string;
    difference?: number | string;
    title?: string;
    config?: { [key: string]: any };
    variables: { [key: string]: string };
    isMultiRow?: boolean;
    /** Actual vs target: with a number value and a number target, "av {target}" and a meter. */
    target?: number | string;
}

type IconNames = "FlickUp" | "FlickDown";

const getDiff = (props: IProps): any => {
    if (typeof props.difference === "undefined") {
        return;
    }

    const primaryValue = props.primaryValue as string;

    const getExtraDiffText = () => {
        if (typeof props.difference === "string" && props.difference.indexOf("%") > -1) {
            return "";
        }

        const percent = formatDiffPercent(primaryValue, props.difference);

        return percent ? " (" + percent + ")" : "";
    }

    if (typeof props.difference === "string") {
        if (props.difference.indexOf("%") > -1) {
            return {
                diff: props.difference,
                positive: props.difference.substr(0, 1) !== "-",
            }
        } else if (props.difference.substr(0, 1) === "-") {
            return {
                diff: formatKeyFigure(Math.abs(parseFloat(props.difference))).number + getExtraDiffText(),
                positive: false,
            };
        }
    }

    const value = typeof props.primaryValue === "number"
        ? props.primaryValue
        : parseFloat(
            (primaryValue || "").replace(/\D+/gmi, "")
        ) || 0;

    // Keeps the decimals ("0.2" was read as 2)
    const refValue = parseKeyNumber(props.difference) || 0;

    const isPercentage = (
        props.config?.isPercentage || (
            typeof props.difference === "string" &&
            props.difference.indexOf("%") > -1
        ));

    return {
        diff: formatKeyFigure(Math.abs(refValue)).number + getExtraDiffText(),
        positive: refValue > -1,
    };
};

// One fixed number size (never scaled to fit), the unit next to it or below when it doesn't fit
const KeyPointIndicatorWidgetPrimaryValue = (props) => {
    const { number, unit: valueUnit } = formatKeyFigure(props.value);
    const unit = props.unit || valueUnit;

    return (
        <Label
            as="div"
            className="number"
            size="h1"
            color="visageGray"
        >
            <span className="number-value">{number}</span>
            {!!unit && <span className="number-unit">{unit}</span>}
        </Label>
    )
}

const KeyPointIndicatorWidgetPrimaryValueTitle = (props) => {
    return (
        <Label className="title" color="mid-grey" weight="medium">{props.title}</Label>
    )
}

const KeyPointIndicatorWidget: FunctionComponent<IProps> = (props) => {
    const usingDiff = typeof props.difference !== "undefined";
    const diffData = usingDiff ? getDiff(props) : undefined;

    const isImprovement = diffData && (diffData.positive
        ? !props.config?.lessIsBetter
        : !!props.config?.lessIsBetter);

    const multiColumn = Array.isArray(props.primaryValue);

    const hideArrow = props.variables?.hideArrow === "1";

    // Actual vs target: only when both are numbers (B11 Mätare)
    const target = typeof props.target === "number" ? props.target : undefined;
    const hasTarget = !multiColumn &&
        typeof props.primaryValue === "number" &&
        target !== undefined &&
        target > 0;
    const targetUnit = hasTarget ?
        localize("AvAntal") + " " + formatKeyFigure(target).number :
        undefined;

    // Rows in a multi widget and columns keep their title above the number;
    // a single key figure has the customer's label under it
    const labelAbove = !!props.isMultiRow || multiColumn;

    // The sign stays when the arrow is hidden: the change is never told by colour alone
    // A minus sign (U+2212), as Intl writes the percentage after it
    const signedDiff = !diffData || /^[+\-\u2212]/.test(String(diffData.diff))
        ? diffData?.diff
        : (diffData.positive ? "+" : "\u2212") + diffData.diff;

    return (
        <div className={["KeyPointIndicatorWidget", ...(multiColumn ? ["multiColumn"] : [])].join(" ")}>
            <div>
                {labelAbove && (!!props.title || props.secondaryValue) && (
                    <KeyPointIndicatorWidgetPrimaryValueTitle title={props.title || props.secondaryValue} />
                )}
                {!multiColumn && (
                    <KeyPointIndicatorWidgetPrimaryValue value={props.primaryValue} unit={targetUnit} />
                )}
                {hasTarget && (
                    <Meter
                        className="KeyPointIndicatorWidget-meter"
                        value={props.primaryValue as unknown as number}
                        max={target}
                        valueText={formatKeyFigure(props.primaryValue).number + " " + targetUnit}
                        label={props.title || props.secondaryValue || undefined} />
                )}
                {!labelAbove && (!!props.title || !!props.secondaryValue) && (
                    <CaptionBody as="div" className="label" color="contentNormal">
                        {props.title || props.secondaryValue}
                    </CaptionBody>
                )}
                {!!multiColumn && (
                    <div className="KeyPointIndicatorWidget-columns">
                        {
                            (props.primaryValue as string[]).map((value, index) => {
                                return (
                                    <div key={index}>
                                        <KeyPointIndicatorWidgetPrimaryValueTitle
                                            title={!!props.variables.columns && index < props.variables.columns.length ?
                                                props.variables.columns[index] :
                                                undefined} />
                                        <KeyPointIndicatorWidgetPrimaryValue value={value} />
                                    </div>
                                )
                            })
                        }
                    </div>
                )}
            </div>
            {usingDiff && (
                <Label
                    className={
                        "text difference " + (
                            isImprovement ? "green" : "red"
                        ) + (hideArrow ? " noArrow" : "")
                    }
                    size="body-2"
                >
                    {!hideArrow && (
                        <div className="iconWrapper">
                            <Visage2Icon icon="arrow-up-3" size="small" color="red" />
                        </div>
                    )}
                    <SmallBodyBolder as="span" weight="semiBold" className="difference-value">
                        {signedDiff}
                    </SmallBodyBolder>
                    {!!(diffData.positive ? props.config?.posDiffText : props.config?.negDiffText) && (
                        <SmallBody as="span" color="contentNormal" className="difference-text">
                            {diffData.positive ? props.config?.posDiffText : props.config?.negDiffText}
                        </SmallBody>
                    )}
                </Label>
            )}
        </div>
    );
}

export default KeyPointIndicatorWidget;
